import {
  ConversationMessage,
  ConversationParticipant,
  StartConversationParams,
} from '@stripe-internal/conversations-client';
import {ConversationsApiDataMessage} from '../generated/types';
import {ApiResponse} from './ApiMethods';
import {FrameworkAdapter} from './FrameworkAdapter';

type PollConversationDeps = {
  pollConversation: (
    conversationId: string,
  ) => ApiResponse<ConversationsApiDataMessage[]>;
};

type PollConversationParams = {
  conversationId: string;
  participantsById: Record<string, ConversationParticipant>;
  user?: NonNullable<StartConversationParams['participant']>;
};

type PollConversationResult =
  | {
      type: 'success';
      messages: Record<string, ConversationMessage>;
      shouldPoll: boolean;
    }
  | {type: 'error'; error: string; code: number};

export class PollConversationHandler {
  private _deps: PollConversationDeps;

  constructor(deps: PollConversationDeps) {
    this._deps = deps;
  }

  async call(params: PollConversationParams): Promise<PollConversationResult> {
    const {conversationId, participantsById, user} = params;

    if (!conversationId) {
      return {type: 'error', error: 'Missing conversationId', code: 400};
    }

    const response = await this._deps.pollConversation(conversationId);

    if (response.type === 'error') {
      return {type: 'error', error: response.error, code: response.code};
    }

    const shouldPoll =
      response.data.length === 0 ||
      response.data.some(
        (msg) =>
          msg.dynamic === 'if_latest' ||
          msg.dynamic === 'active' ||
          msg.parts.some(
            (p) => p.dynamic === 'if_latest' || p.dynamic === 'active',
          ),
      );

    const messages = FrameworkAdapter.fromApiMessages(
      response.data,
      participantsById,
      user,
    );

    return {type: 'success', messages, shouldPoll};
  }
}
